import React, { useState } from "react";
import { cn } from "~/lib/utils";
import { type VariantProps, cva } from "class-variance-authority";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";

// Upgraded modal implementation using Radix UI Dialog for better accessibility

// Legacy trigger and close components for compatibility
interface TriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

const Trigger = ({ children, ...props }: TriggerProps) => {
  // These are now handled by Dialog components, but keeping for backwards compatibility
  return <button {...props}>{children}</button>;
};

interface RootProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
}

const Root = ({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
}: RootProps) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen);
    }
    onOpenChange?.(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children}
    </Dialog>
  );
};

export const panelVariants = cva(
  [
    "w-full transition-all backdrop:brightness-50 backdrop:backdrop-blur-sm",
    "data-[open]:flex flex-col gap-4 border bg-card p-6 sm:rounded-lg",
    "data-[closing]:duration-300 data-[open]:duration-300 data-[open]:animate-in data-[closing]:animate-out",
    "backdrop:data-[closing]:duration-300 backdrop:data-[open]:duration-300 backdrop:data-[open]:animate-in backdrop:data-[closing]:animate-out backdrop:data-[closing]:fade-out backdrop:data-[open]:fade-in",
  ],
  {
    variants: {
      position: {
        center:
          "rounded-base shadow-lg data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2 backdrop:data-[closing]:fade-out backdrop:data-[open]:fade-in",
        top: "inset-x-0 top-0 mt-0 rounded-b-base border-b data-[closing]:slide-out-to-top data-[open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 mb-0 rounded-t-base border-t data-[closing]:slide-out-to-bottom data-[open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 ml-0 h-full max-w-sm rounded-r-base border-r data-[closing]:slide-out-to-left data-[open]:slide-in-from-left",
        right:
          "inset-y-0 right-0 mr-0 h-full max-w-sm rounded-l-base border-l data-[closing]:slide-out-to-right data-[open]:slide-in-from-right",
      },
      size: {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        auto: "max-w-fit",
      },
    },
    defaultVariants: {
      position: "center",
      size: "lg",
    },
  },
);

type PanelProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof panelVariants> & {
    children: React.ReactNode;
  };

const Panel = ({
  position,
  size,
  children,
  className,
  ...props
}: PanelProps) => {
  return (
    <DialogContent
      {...props}
      className={cn(panelVariants({ position, size }), className)}
    >
      {children}
    </DialogContent>
  );
};

interface TitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children: React.ReactNode;
}

const Title = ({ children, className, ...props }: TitleProps) => {
  return (
    <DialogTitle
      {...props}
      className={cn("font-heading text-lg tracking-tight", className)}
    >
      {children}
    </DialogTitle>
  );
};

interface DescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children: React.ReactNode;
}

const Description = ({ children, className, ...props }: DescriptionProps) => {
  return (
    <DialogDescription
      {...props}
      className={cn("text-muted-foreground text-sm", className)}
    >
      {children}
    </DialogDescription>
  );
};

export const Modal = {
  Root,
  Trigger,
  Panel,
  Title,
  Description,
};
